import React, { useState, useEffect } from "react";
import {
  Button,
  CssBaseline,
  TextField,
  Box,
  Container,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { toastAlert } from "../../utils/AlertContext";

const ROLE_REDIRECTS = {
  admin: "/admin",
  editor: "/editor",
  usuario: "/client",
};

const Login = () => {
  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const { login, token, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (token && user?.rol) {
      navigate(ROLE_REDIRECTS[user.rol]);
    }
  }, [token, user, navigate, location]);

  const validateForm = () => {
    const { email, password } = credentials;

    if (!email || !password) {
      return "Todos los campos son obligatorios";
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return "Ingrese un email válido";
    }

    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      toastAlert(validationError, "error");
      return;
    }

    try {
      setLoading(true);

      const response = await login(credentials.email, credentials.password);

      if (!response?.success) {
        throw new Error(response?.message || "Error en la autenticación");
      }
    } catch (err) {
      console.error("Login error:", err);
      toastAlert(err.message || "Credenciales incorrectas. Intente nuevamente.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        backgroundSize: "cover",
        backgroundPosition: "center",
        p: 2,
      }}
    >
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            bgcolor: "background.paper",
            p: 4,
            borderRadius: 2,
            boxShadow: 3,
          }}
        >
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Typography variant="h4" component="h1" fontWeight="bold" color="primary" gutterBottom>
              EDITOR{" "}
              <Typography component="span" sx={{ fontSize: "0.7em", color: "error.main", verticalAlign: "middle" }}>
                v.26
              </Typography>
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Sistema de Gestión Editorial (SIGEDI)
            </Typography>
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ width: "100%" }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Correo Electrónico"
              name="email"
              autoComplete="email"
              autoFocus
              value={credentials.email}
              onChange={handleChange}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Contraseña"
              type="password"
              id="password"
              autoComplete="current-password"
              value={credentials.password}
              onChange={handleChange}
              disabled={loading}
              sx={{ mb: 3 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ py: 1.5 }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Iniciar sesión"
              )}
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
